import { useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router'
import { useAuth } from '../contexts/AuthContext'
import { teamApiService } from '../services/teamApiService'
import { ApiError } from '../services/BaseApiService'
import { SecurityRole } from '../constants'
import { colors, fontSize, radius, spacing } from '../styles/theme'

/** Pages a closed team can still use: its final bill and payment method, the account's own settings, and leaving. */
export const CLOSED_TEAM_ALLOWED = ['/app/billing', '/app/settings', '/app/team']

export function formatDay(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : ''
}

/**
 * Shown across the app while the team is closed (V085): when its data will be erased, and — for a Team Admin —
 * the button that reopens it. Renders nothing for an open team.
 */
export function TeamClosedBanner() {
  const { user, refresh } = useAuth()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  if (!user?.teamClosedAt) return null
  const isAdmin = (user.securityRole ?? 0) >= SecurityRole.TEAM_ADMIN

  async function reopen() {
    setBusy(true); setError('')
    try { await teamApiService.reopen(); await refresh() }
    catch (err) { setError(err instanceof ApiError ? err.message : 'Reopening failed.') }
    finally { setBusy(false) }
  }

  return (
    <div role="alert" style={{
      background: colors.errorLight, color: colors.error, borderBottom: `1px solid ${colors.errorBorder}`,
      padding: `${spacing.sm} ${spacing.lg}`, fontSize: fontSize.sm, display: 'flex', gap: spacing.md, alignItems: 'center', flexWrap: 'wrap',
    }}>
      <span style={{ flex: '1 1 260px' }}>
        <strong>{user.teamName ?? 'This team'} is closed.</strong> Rendering, API keys and connectors have stopped.
        {user.teamEraseAfter ? ` Its data will be erased after ${formatDay(user.teamEraseAfter)}.` : ''}
        {!isAdmin ? ' Only a Team Admin can reopen it.' : ''}
      </span>
      {error && <span>{error}</span>}
      {isAdmin && (
        <button onClick={reopen} disabled={busy} style={{
          padding: '4px 12px', background: colors.surface, color: colors.error, border: `1px solid ${colors.errorBorder}`,
          borderRadius: radius.sm, fontSize: fontSize.sm, fontWeight: 600, cursor: 'pointer', opacity: busy ? 0.6 : 1,
        }}>
          {busy ? 'Reopening…' : 'Reopen team'}
        </button>
      )}
    </div>
  )
}

/**
 * The routed page, or — for a closed team on any page that does work — a notice of what is still reachable. Keeps a
 * closed team's pages from filling with 403 TEAM_CLOSED errors.
 */
export function ClosedTeamGate({ outletKey }: { outletKey?: string }) {
  const { user } = useAuth()
  const { pathname } = useLocation()
  if (!user?.teamClosedAt || CLOSED_TEAM_ALLOWED.some(p => pathname.startsWith(p))) return <Outlet key={outletKey} />
  return (
    <div style={{ maxWidth: 560 }}>
      <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: colors.text, marginTop: 0 }}>This team is closed</h1>
      <p style={{ fontSize: fontSize.base, color: colors.textSecondary }}>
        Its templates, documents and keys are kept until {formatDay(user.teamEraseAfter) || 'the end of the 30-day window'}, then
        permanently erased. Until then you can still see the final bill, update the payment method, change your account
        settings or leave the team.
      </p>
      <div style={{ display: 'flex', gap: spacing.md, flexWrap: 'wrap' }}>
        <Link to="/app/billing" style={{ color: colors.primary, fontWeight: 600 }}>Billing</Link>
        <Link to="/app/team" style={{ color: colors.primary, fontWeight: 600 }}>Team</Link>
        <Link to="/app/settings" style={{ color: colors.primary, fontWeight: 600 }}>Account settings</Link>
      </div>
    </div>
  )
}
